import { emitSessionExpired } from '../auth/sessionEvents';
import { clearTokens, getRefreshToken, setTokens } from '../auth/tokenStorage';
import type { TokenPair } from '../types/api';

import { unwrapNestedData } from './unwrap';

const baseUrl = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:4000';

let refreshInFlight: Promise<string | null> | null = null;

async function requestRefreshToken(): Promise<string | null> {
    const refreshToken = getRefreshToken();
    if (!refreshToken) {
        return null;
    }

    for (let attempt = 0; attempt < 3; attempt += 1) {
        if (attempt > 0) {
            await new Promise((resolve) => setTimeout(resolve, 500 * attempt));
        }
        try {
            const res = await fetch(`${baseUrl}/authentication/refresh`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ refresh_token: getRefreshToken() }),
            });

            // A server response means the session is genuinely rejected —
            // retrying won't help. Only retry network-level failures.
            if (!res.ok) {
                return null;
            }

            const json: unknown = await res.json();
            const tokens = unwrapNestedData<TokenPair>(json);

            if (!tokens?.access_token || !tokens?.refresh_token) {
                return null;
            }

            setTokens(tokens);
            return tokens.access_token;
        } catch {
            // transient network failure — retry
        }
    }
    return null;
}

/** Refresh the access token once; concurrent callers share the same in-flight request. */
export function refreshAccessToken(): Promise<string | null> {
    if (!refreshInFlight) {
        refreshInFlight = requestRefreshToken().finally(() => {
            refreshInFlight = null;
        });
    }
    return refreshInFlight;
}

export function expireSession(): void {
    clearTokens();
    emitSessionExpired();
}
